import React, { useRef, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { Card, Empty, Flex, Typography, theme } from 'antd';

import {
  useGetSearchPerformancePathsQuery,
  type SearchPerformancePathRow,
  type SearchPerformancePathSortBy,
} from '../api/searchPerformanceApi';
import type { SearchPerformancePathSortColumn } from '../SearchPerformanceAnalyticsEvents';
import { formatSectionPath, usePagesSectionNavigation } from '../pages';
import { getReportingWindow } from '../SearchPerformanceReportingWindow';
import SearchPerformanceRequestError from '../SearchPerformanceRequestError';
import { toRequestLoad } from '../SearchPerformanceRequestLoad';
import SearchPerformanceTableSkeleton from '../SearchPerformanceTableSkeleton';
import useSearchPerformanceProperty from '../useSearchPerformanceProperty';
import useTrackSearchPerformanceEvent from '../useTrackSearchPerformanceEvent';
import useWidgetLoadTelemetry from '../useWidgetLoadTelemetry';
import PerformanceByPathTable, {
  PERFORMANCE_BY_PATH_PAGE_SIZE,
  type PerformanceByPathRow,
  type PerformanceByPathSort,
  type PerformanceByPathSortField,
  type PerformanceByPathTableChange,
} from './PerformanceByPathTable';

const PERFORMANCE_BY_PATH_STRINGS = {
  title: 'Performance by path',
  subtitle:
    'The same three buckets as above, per section. Each row is a first-level path (e.g. /docs/, /blog/) — pages under deeper paths roll up into it, so rows do not overlap. Sorted by the biggest "Crawled, no impressions" bucket, because that is the largest block of work.',
  loadFailed: 'Unable to load the path table.',
  emptyDescription: 'No data available',
};

const DEFAULT_SORT: PerformanceByPathSort = { field: 'crawledInvisible', order: 'desc' };

const API_SORT_BY: Record<PerformanceByPathSortField, SearchPerformancePathSortBy> = {
  path: 'path',
  crawledVisible: 'crawledWithImpressions',
  crawledInvisible: 'crawledNoImpressions',
  notServed: 'notServedWithImpressions',
  ctr: 'ctr',
  errors: 'errors',
};

// Internal sort fields → the ticket's Mixpanel column vocabulary (snake_case,
// and unlike the BFF's camelCase sort keys above).
const PATH_SORT_COLUMN: Record<PerformanceByPathSortField, SearchPerformancePathSortColumn> = {
  path: 'path',
  crawledVisible: 'crawled_with_impressions',
  crawledInvisible: 'crawled_no_impressions',
  notServed: 'not_prerender_served_with_impressions',
  ctr: 'ctr',
  errors: 'delivery_errors',
};

// API is 0–1; table is percent.
function toCtrPercent(ctr: number | null): number | null {
  return ctr === null ? null : Math.round(ctr * 1000) / 10;
}

function toTableRow(row: SearchPerformancePathRow): PerformanceByPathRow {
  return {
    key: row.path,
    path: formatSectionPath(row.path),
    crawledVisible: row.crawledWithImpressions,
    crawledInvisible: row.crawledNoImpressions,
    notServed: row.notServedWithImpressions,
    ctr: toCtrPercent(row.ctr),
    errors: row.errors,
  };
}

function PerformanceByPathSection() {
  const { token } = theme.useToken();

  const { selectedProperty, isLoading: isPropertyLoading } = useSearchPerformanceProperty();
  const { dateStart, dateEnd } = getReportingWindow();
  const navigateToPages = usePagesSectionNavigation();
  const trackEvent = useTrackSearchPerformanceEvent();
  const [sort, setSort] = useState<PerformanceByPathSort>(DEFAULT_SORT);
  const [page, setPage] = useState(1);

  // A new property is a fresh view: page and sort both reset. (Page 5, or a
  // non-default sort, of one property is meaningless for another; a stale offset
  // against a smaller total renders an empty page.) Adjust during render so the
  // first request of a new property already carries page 1 and the default sort;
  // an effect would dispatch the previous values first.
  const propertyUrl = selectedProperty?.propertyUrl;
  const [scopedPropertyUrl, setScopedPropertyUrl] = useState(propertyUrl);
  const isNewProperty = scopedPropertyUrl !== propertyUrl;
  if (isNewProperty) {
    setScopedPropertyUrl(propertyUrl);
    setPage(1);
    setSort(DEFAULT_SORT);
  }
  const queryPage = isNewProperty ? 1 : page;
  const querySort = isNewProperty ? DEFAULT_SORT : sort;
  const sortBy = API_SORT_BY[querySort.field];
  const offset = (queryPage - 1) * PERFORMANCE_BY_PATH_PAGE_SIZE;

  const pathsQuery = useGetSearchPerformancePathsQuery(
    selectedProperty
      ? {
          dateStart,
          dateEnd,
          property: selectedProperty.propertyUrl,
          sortBy,
          sortOrder: querySort.order,
          limit: PERFORMANCE_BY_PATH_PAGE_SIZE,
          offset,
        }
      : skipToken
  );
  const { data, currentData, isError, isFetching, isUninitialized, refetch } = pathsQuery;
  const { trackRetryClicked } = useWidgetLoadTelemetry({
    property: propertyUrl,
    reportingWindow: { dateStart, dateEnd },
    interactionQueryArgs: [sortBy, querySort.order, offset],
    load: toRequestLoad(pathsQuery),
    loadedProperties: { widget: 'paths', has_data: (currentData?.total ?? 0) > 0 },
    failedProperties: { widget: 'paths' },
  });

  // `data` is the last result for any arg; `currentData` is this arg's.
  // Keep `data` across sort/page so the table overlays instead of skeletonizing.
  // Drop it on a property change — those rows belong to a different host.
  const lastFulfilledPropertyUrl = useRef<string | undefined>(undefined);
  if (currentData !== undefined) {
    lastFulfilledPropertyUrl.current = propertyUrl;
  }
  const staleDataBelongsToCurrentProperty =
    lastFulfilledPropertyUrl.current === undefined || lastFulfilledPropertyUrl.current === propertyUrl;
  const resolved = currentData ?? (staleDataBelongsToCurrentProperty ? data : undefined);

  // Gated like KpiSection's: under `skipToken` none of the query terms settle,
  // and the query dispatches from an effect, so it is merely uninitialised on
  // the render where the arg first appears. `resolved` retains the last
  // fulfilled value across sort/page changes, so the skeleton only covers the
  // first load and a property switch — refetches show as the table's overlay.
  const isPending = isPropertyLoading || (!!selectedProperty && (isUninitialized || (isFetching && !resolved)));
  const showEmpty = !isPending && !isError && (resolved?.total ?? 0) === 0;

  const handleRetry = () => {
    trackRetryClicked();
    refetch();
  };

  const handleTableChange = ({ action, page: nextPage, sort: nextSort }: PerformanceByPathTableChange) => {
    if (action === 'sort' && nextSort) {
      trackEvent('Search Performance: Path Sort Changed', {
        column: PATH_SORT_COLUMN[nextSort.field],
        direction: nextSort.order,
      });
    }
    setSort(nextSort ?? DEFAULT_SORT);
    setPage(nextPage);
  };

  return (
    <Flex vertical gap={token.marginXS}>
      <Flex vertical>
        <Typography.Title
          level={3}
          style={{ margin: 0, fontSize: token.fontSizeHeading4, fontWeight: token.fontWeightStrong }}
        >
          {PERFORMANCE_BY_PATH_STRINGS.title}
        </Typography.Title>
        <Typography.Text type="secondary" style={{ display: 'block', maxWidth: 640 }}>
          {PERFORMANCE_BY_PATH_STRINGS.subtitle}
        </Typography.Text>
      </Flex>

      {isError && (
        <SearchPerformanceRequestError message={PERFORMANCE_BY_PATH_STRINGS.loadFailed} onRetry={handleRetry} />
      )}

      {(isPending || showEmpty || resolved) && (
        <Card>
          {isPending ? (
            // This table's long column titles ("Not Prerender-served, with
            // impressions") wrap to three lines even on wide screens, so the
            // skeleton reserves a three-line header to match.
            <SearchPerformanceTableSkeleton headerRows={3} rowCount={PERFORMANCE_BY_PATH_PAGE_SIZE} />
          ) : showEmpty ? (
            <Empty description={PERFORMANCE_BY_PATH_STRINGS.emptyDescription} />
          ) : (
            <PerformanceByPathTable
              rows={(resolved?.rows ?? []).map(toTableRow)}
              loading={isFetching}
              page={queryPage}
              total={resolved?.total ?? 0}
              sort={querySort}
              onChange={handleTableChange}
              onReview={(sectionKey) => {
                const path = formatSectionPath(sectionKey);
                trackEvent('Search Performance: Path Row Reviewed', { path_prefix: path });
                navigateToPages({ search: path });
              }}
            />
          )}
        </Card>
      )}
    </Flex>
  );
}

export default PerformanceByPathSection;
